import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImageIcon, XIcon } from "@phosphor-icons/react";
import { ChatAttachButton } from "@/zapi-frontend/shared/ui/chat";
import type { TLeoMessageImage } from "@/zapi-frontend/stores/leoChatStore";
import { LEO_IMAGE_TYPES, type TStagedLeoImage } from "../hooks/useLeoImages";

export function LeoAttachImageButton({ onFiles, disabled }: { onFiles: (files: File[]) => void; disabled: boolean }) {
  const { t } = useTranslation("projectHome");
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <ChatAttachButton
        label={t("leo.images.attach")}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        data-testid="leo-chat-attach-image"
      />
      <input
        ref={inputRef}
        type="file"
        accept={LEO_IMAGE_TYPES.join(",")}
        multiple
        hidden
        onChange={(e) => {
          onFiles(Array.from(e.target.files ?? []));
          // Cleared so picking the same file again still fires a change.
          e.target.value = "";
        }}
      />
    </>
  );
}

export function LeoStagedImages({ images, onRemove }: { images: TStagedLeoImage[]; onRemove: (id: string) => void }) {
  const { t } = useTranslation("projectHome");
  if (images.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-3 px-3 pt-3" data-testid="leo-chat-staged-images">
      {images.map((image) => (
        <div key={image.id} className="relative size-14">
          <img
            src={image.previewUrl}
            alt={image.file.name}
            className="size-full rounded-[6px] border border-innoveraZinc-150 object-cover"
          />
          {/* Solid and on the corner, so it reads on any image, light or dark. */}
          <button
            type="button"
            aria-label={t("leo.images.remove", { name: image.file.name })}
            title={t("leo.images.remove", { name: image.file.name })}
            onClick={() => onRemove(image.id)}
            className="absolute -right-2 -top-2 flex size-5 items-center justify-center rounded-full border border-innoveraZinc-150 bg-white text-innoveraZinc-900 shadow-xs hover:bg-innoveraZinc-100"
            data-testid="leo-chat-remove-image"
          >
            <XIcon className="size-3" weight="bold" />
          </button>
        </div>
      ))}
    </div>
  );
}

function MessageImage({ image }: { image: TLeoMessageImage }) {
  const { t } = useTranslation("projectHome");
  const [failed, setFailed] = useState(false);
  if (!image.url || failed) {
    return (
      <span
        title={t("leo.images.unavailable")}
        className="flex items-center gap-1 rounded-[6px] bg-white px-2 py-1 text-[12px] leading-[18px] text-innoveraZinc-500"
      >
        <ImageIcon className="size-4 shrink-0" />
        <span className="truncate">{image.name}</span>
      </span>
    );
  }
  return (
    <a
      href={image.url}
      target="_blank"
      rel="noreferrer"
      title={image.name}
      className="block overflow-hidden rounded-[6px] border border-innoveraZinc-150 bg-white"
    >
      <img
        src={image.url}
        alt={image.name}
        onError={() => setFailed(true)}
        className="max-h-40 max-w-full object-contain"
      />
    </a>
  );
}

export function LeoMessageImages({ images }: { images: TLeoMessageImage[] }) {
  return (
    <div className="flex flex-wrap justify-end gap-1.5" data-testid="leo-chat-message-images">
      {images.map((image, index) => (
        <MessageImage key={index} image={image} />
      ))}
    </div>
  );
}
